import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AssociationManager } from '../associations/AssociationManager';
import { User } from '@/types/user.types';
import { Device, DeviceAccessType } from '@/types/device.types';
import { deviceService } from '@/services/api/services/deviceService';
import { userService } from '@/services/api/services/userService';
import { associationService } from '@/services/api/services/associationService';
import { AssociationWithDetailsResponse } from '@/types/association.types';
import { useNavigationHelpers } from '@/utils/navigationHelpers';

interface DeviceUserManagerProps {
  deviceId: string;
}

export const DeviceUserManager: React.FC<DeviceUserManagerProps> = ({ deviceId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { navigateWithTokenCheck } = useNavigationHelpers();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [device, setDevice] = useState<Device | null>(null);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [associations, setAssociations] = useState<
    Array<{ userId: string; typeOfAccess: DeviceAccessType }>
  >([]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const [deviceResponse, usersResponse, associationsResponse] = await Promise.all([
        deviceService.getById(deviceId),
        userService.getAllForAssociations(),
        associationService.getDeviceAssociationsWithUsers(deviceId),
      ]);

      if (!deviceResponse.success) {
        throw new Error(deviceResponse.error.message || t('errors.deviceNotFound'));
      }

      if (!usersResponse.success) {
        throw new Error(usersResponse.error.message || t('errors.usersNotFound'));
      }

      if (!associationsResponse.success) {
        throw new Error(associationsResponse.error.message || t('errors.associationsNotFound'));
      }

      setDevice(deviceResponse.data);
      setAllUsers(usersResponse.data);
      setAssociations(
        associationsResponse.data.map((assoc: AssociationWithDetailsResponse) => ({
          userId: assoc.userId,
          typeOfAccess: assoc.typeOfAccess as DeviceAccessType,
        }))
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.unknown'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const initializeData = async () => {
      await fetchData();
    };

    initializeData();

    return () => {
      isMounted = false;
    };
  }, [deviceId, t]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error || !device) {
    return (
      <div className="min-h-screen bg-brand-white p-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 text-red-600 p-4 rounded-md">
            {error || t('errors.deviceNotFound')}
          </div>
          <button
            onClick={() => navigateWithTokenCheck(navigate, '/devices')}
            className="mt-4 bg-brand-white text-brand-orange border border-brand-orange hover:bg-brand-orange hover:text-brand-white px-4 py-2 rounded-md transition-colors duration-200"
          >
            {t('common.goBack')}
          </button>
        </div>
      </div>
    );
  }

  const currentAssociations = associations.map((access) => {
    const user = allUsers.find((u) => u.id === access.userId);
    return {
      id: access.userId,
      name: user?.credentials?.username || access.userId,
      typeOfAccess: access.typeOfAccess as DeviceAccessType,
    };
  });

  const availableEntities = allUsers
    .filter((user) => !associations.some((assoc) => assoc.userId === user.id))
    .map((user) => ({
      id: user.id || '',
      name: user.credentials?.username || '',
      description: `${user.personalInfo?.name || ''} ${user.personalInfo?.surname1 || ''}`.trim(),
    }));

  const entityDetails = {
    id: device.id || '',
    name: device.serialNumber || '',
    description: device.description || device.alias || '',
  };

  return (
    <div className="min-h-screen bg-brand-white p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-brand-gray-dark">
            {t('pages.devices.manageUsersTitle')}
          </h1>
          <div className="mt-2">
            <span className="text-brand-gray-dark font-medium">Número de serie: </span>
            <span className="text-brand-orange">{device?.serialNumber}</span>
            {device?.alias && (
              <>
                <br />
                <span className="text-brand-gray-dark font-medium">Alias del dispositivo: </span>
                <span className="text-brand-orange">{device.alias}</span>
              </>
            )}
          </div>
        </div>

        <AssociationManager
          entity={entityDetails}
          entityType="device"
          currentAssociations={currentAssociations.map((assoc) => ({
            ...assoc,
            displayInfo: {
              mainLabel: 'Nombre de usuario:',
              mainValue: assoc.name,
              subLabel: 'Nombre y apellido:',
              subValue: allUsers.find((u) => u.id === assoc.id)?.personalInfo
                ? `${allUsers.find((u) => u.id === assoc.id)?.personalInfo?.name || ''} , ${
                    allUsers.find((u) => u.id === assoc.id)?.personalInfo?.surname1 || ''
                  }`.trim()
                : '',
            },
          }))}
          availableEntities={availableEntities.map((entity) => ({
            ...entity,
            displayInfo: {
              mainLabel: 'Nombre de usuario:',
              mainValue: entity.name,
              subLabel: 'Nombre y apellido:',
              subValue: entity.description || '',
            },
          }))}
          onCancel={() => navigateWithTokenCheck(navigate, '/devices')}
          onAssociationChange={async (type, associationId) => {
            console.log(`Association ${type}:`, associationId);
            // Refresh data after association changes to update available entities
            await fetchData();
          }}
        />
      </div>
    </div>
  );
};
